import React, { useState, useEffect, useRef } from 'react';
import { PublicHTTPService } from '../../../services/publicHttpService';
import BookmarkThisPage from '../../partial/BookmarkThisPage';
import JobviousLogo from "./../../../assets/Jobvious_logo@2x.svg";

const API = new PublicHTTPService();
const SEARCH_DEBOUNCE_MS = 250;
const SIGNUP_URL = '/signup/dealer?source=assist';

const PublicReferralForm = () => {
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({
    referring_email: '',
    customer_name: '',
    customer_email: '',
    vin: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  // Dealer typeahead
  const [dealerQuery, setDealerQuery] = useState('');
  const [dealerResults, setDealerResults] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [dealerLoading, setDealerLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Assist mode (set when ?dealer_id resolves to an Assist dealer)
  const [assistMode, setAssistMode] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageLoadError, setPageLoadError] = useState(null);

  const dealerInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);

  // On mount: handle ?dealer_id pre-selection
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dealerId = params.get('dealer_id');
    if (!dealerId) return;

    setPageLoading(true);
    API.get(`/public/dealers/${dealerId}`)
      .then(response => {
        const dealer = response.data.dealer || response.data;
        const dealerObj = {
          dealer_id: dealer.id,
          dealer_name: dealer.name,
          city: dealer.city,
          state: dealer.state
        };
        setSelectedDealer(dealerObj);
        setDealerQuery(dealer.name);
        if (dealer.attribution_source === 'assist') {
          setAssistMode(true);
        }
      })
      .catch(err => {
        console.error('Failed to load dealer:', err);
        setPageLoadError("We couldn't load that dealership. You can still search for it below.");
      })
      .finally(() => setPageLoading(false));
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        dealerInputRef.current && !dealerInputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced typeahead search
  useEffect(() => {
    if (assistMode) return;
    if (!dealerQuery || dealerQuery.length < 2) {
      setDealerResults([]);
      setDealerLoading(false);
      return;
    }
    if (selectedDealer && dealerQuery === selectedDealer.dealer_name) {
      // Don't re-search the value we just selected
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDealerLoading(true);
      API.get('/public/dealers', { q: dealerQuery, limit: 20 })
        .then(response => {
          const list = response.data.dealers || response.data || [];
          setDealerResults(list);
          setDealerLoading(false);
          setHighlightedIndex(-1);
        })
        .catch(err => {
          console.error('Dealer search failed:', err);
          setDealerResults([]);
          setDealerLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [dealerQuery, assistMode, selectedDealer]);

  const handleDealerQueryChange = (e) => {
    const value = e.target.value;
    setDealerQuery(value);
    setShowDropdown(true);
    if (selectedDealer && value !== selectedDealer.dealer_name) {
      setSelectedDealer(null);
    }
    if (errors.dealer) {
      setErrors(prev => ({ ...prev, dealer: '' }));
    }
  };

  const handleDealerSelect = (dealer) => {
    setSelectedDealer({
      dealer_id: dealer.id,
      dealer_name: dealer.name,
      city: dealer.city,
      state: dealer.state
    });
    setDealerQuery(dealer.name);
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleSentinelClick = () => {
    window.open(SIGNUP_URL, '_blank', 'noopener');
    setShowDropdown(false);
  };

  const handleDealerKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === 'ArrowDown' && dealerQuery.length >= 2) {
        setShowDropdown(true);
      }
      return;
    }
    const sentinelIndex = dealerResults.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, sentinelIndex));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlightedIndex < 0) return;
      e.preventDefault();
      if (highlightedIndex === sentinelIndex) {
        handleSentinelClick();
      } else if (dealerResults[highlightedIndex]) {
        handleDealerSelect(dealerResults[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const processedValue = name === 'vin' ? value.toUpperCase() : value;
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedDealer) {
      newErrors.dealer = 'Please select your dealership.';
    }

    if (!formData.referring_email.trim()) {
      newErrors.referring_email = 'Your email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.referring_email)) {
      newErrors.referring_email = 'Please enter a valid email address';
    }

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }

    if (!formData.customer_email.trim()) {
      newErrors.customer_email = 'Customer email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      newErrors.customer_email = 'Please enter a valid customer email address';
    }

    if (!formData.vin.trim()) {
      newErrors.vin = 'VIN is required';
    } else if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(formData.vin)) {
      newErrors.vin = 'VIN must be exactly 17 characters (letters and numbers, excluding I, O, Q)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        dealer_id: selectedDealer.dealer_id
      };
      const response = await API.post('/public/referral', payload);

      if (response.data.success) {
        setSubmittedData({
          referring_email: formData.referring_email,
          customer_name: formData.customer_name,
          referralId: response.data.referralId,
          dealer_name: selectedDealer.dealer_name
        });
        setStep('success');
      } else {
        setErrors({ submit: response.data.error || 'Failed to submit referral. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting referral:', error);
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  const renderHeader = () => (
    <div className="bg-white shadow-sm">
      <div className="container py-4">
        <div className="d-flex align-items-center">
          <img
            src={JobviousLogo}
            alt="Jobvious Logo"
            className="img-fluid"
            style={{ maxWidth: "200px", height: "auto" }}
          />
          <small className="text-muted ms-3">
            Bringing Car Dealers &amp; Specialist Installers together
          </small>
        </div>
      </div>
    </div>
  );

  const renderAssistBanner = () => {
    if (!assistMode || !selectedDealer) return null;
    const bookmarkUrl = `${window.location.origin}/public/referral?dealer_id=${selectedDealer.dealer_id}`;
    return (
      <div
        className="card mb-4"
        style={{ borderLeft: '4px solid #FF9516' }}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div className="me-3 mb-2">
              <h5 className="mb-1">
                <i className="fa-sharp fa-solid fa-handshake text-warning me-2" />
                Welcome to Jobvious Assist for {selectedDealer.dealer_name}
              </h5>
              <p className="text-muted mb-0">
                Bookmark this page so you can return any time to submit a customer referral.
              </p>
            </div>
            <BookmarkThisPage url={bookmarkUrl} />
          </div>
        </div>
      </div>
    );
  };

  const renderDealerField = () => {
    if (assistMode && selectedDealer) {
      return (
        <div className="mb-3">
          <label className="form-label">Dealership</label>
          <div
            className="form-control bg-light d-flex align-items-center"
            style={{ cursor: 'not-allowed' }}
          >
            <i className="fa-sharp fa-solid fa-building text-warning me-2" />
            <strong>{selectedDealer.dealer_name}</strong>
            {selectedDealer.city && selectedDealer.state && (
              <small className="text-muted ms-2">
                {selectedDealer.city}, {selectedDealer.state}
              </small>
            )}
          </div>
        </div>
      );
    }

    const sentinelIndex = dealerResults.length;
    return (
      <div className="mb-3 position-relative">
        <label className="form-label">Your Dealership *</label>
        <input
          ref={dealerInputRef}
          type="text"
          value={dealerQuery}
          onChange={handleDealerQueryChange}
          onFocus={() => dealerQuery.length >= 2 && setShowDropdown(true)}
          onKeyDown={handleDealerKeyDown}
          className={`form-control ${errors.dealer ? 'is-invalid' : ''}`}
          placeholder="Start typing your dealership name..."
          autoComplete="off"
        />
        {errors.dealer && <div className="invalid-feedback">{errors.dealer}</div>}
        {!errors.dealer && (
          <small className="text-muted">
            Type at least 2 characters to search registered dealerships.
          </small>
        )}

        {showDropdown && (dealerQuery.length >= 2 || dealerResults.length > 0) && (
          <div
            ref={dropdownRef}
            className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
            style={{
              zIndex: 1050,
              maxHeight: '320px',
              overflowY: 'auto',
              top: '100%'
            }}
          >
            {dealerLoading && (
              <div className="px-3 py-2 text-muted small">
                <span className="spinner-border spinner-border-sm me-2" />
                Searching...
              </div>
            )}

            {!dealerLoading && dealerResults.length === 0 && dealerQuery.length >= 2 && (
              <div className="px-3 py-2 text-muted small">
                No matching dealerships found.
              </div>
            )}

            {dealerResults.map((dealer, idx) => (
              <div
                key={dealer.id}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleDealerSelect(dealer)}
                className="px-3 py-2"
                style={{
                  cursor: 'pointer',
                  backgroundColor: idx === highlightedIndex ? '#f8f9fa' : 'transparent'
                }}
              >
                <div>{dealer.name}</div>
                {(dealer.city || dealer.state) && (
                  <small className="text-muted">
                    {[dealer.city, dealer.state].filter(Boolean).join(', ')}
                  </small>
                )}
              </div>
            ))}

            <div
              onMouseEnter={() => setHighlightedIndex(sentinelIndex)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSentinelClick}
              className="px-3 py-2 border-top"
              style={{
                cursor: 'pointer',
                backgroundColor: highlightedIndex === sentinelIndex ? '#fff3e0' : '#fffaf0',
                color: '#FF9516'
              }}
            >
              <strong>
                <i className="fa-sharp fa-solid fa-plus me-2" />
                Don't see your dealership? Sign up here
              </strong>
              <i className="fa-sharp fa-regular fa-arrow-up-right-from-square ms-2" />
            </div>
          </div>
        )}
      </div>
    );
  };

  if (pageLoading) {
    return (
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-warning mb-3" />
          <p className="text-muted">Loading your referral page...</p>
        </div>
      </div>
    );
  }

  if (step === 'form') {
    return (
      <div className="min-vh-100 bg-light">
        {renderHeader()}

        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {renderAssistBanner()}

              <div className="card shadow">
                <div className="card-body p-5">
                  <div className="text-center mb-5">
                    <div className="text-warning mb-3">
                      <i className="fa-sharp fa-solid fa-handshake fa-3x"></i>
                    </div>
                    <h1 className="h2 mb-3">Submit Customer Referral</h1>
                    <p className="text-muted">
                      Help connect interested customers with automotive specialists in your area
                    </p>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {errors.submit && (
                      <div className="alert alert-danger">
                        {errors.submit}
                      </div>
                    )}

                    {pageLoadError && (
                      <div className="alert alert-warning">
                        {pageLoadError}
                      </div>
                    )}

                    {renderDealerField()}

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Your Email *</label>
                        <input
                          type="email"
                          name="referring_email"
                          value={formData.referring_email}
                          onChange={handleInputChange}
                          className={`form-control ${errors.referring_email ? 'is-invalid' : ''}`}
                          placeholder="your.email@dealer.com"
                        />
                        {errors.referring_email && (
                          <div className="invalid-feedback">{errors.referring_email}</div>
                        )}
                        <small className="text-muted">We'll use this to track your referral</small>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Customer Name *</label>
                        <input
                          type="text"
                          name="customer_name"
                          value={formData.customer_name}
                          onChange={handleInputChange}
                          className={`form-control ${errors.customer_name ? 'is-invalid' : ''}`}
                          placeholder="John Smith"
                        />
                        {errors.customer_name && (
                          <div className="invalid-feedback">{errors.customer_name}</div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Customer Email *</label>
                        <input
                          type="email"
                          name="customer_email"
                          value={formData.customer_email}
                          onChange={handleInputChange}
                          className={`form-control ${errors.customer_email ? 'is-invalid' : ''}`}
                          placeholder="customer@email.com"
                        />
                        {errors.customer_email && (
                          <div className="invalid-feedback">{errors.customer_email}</div>
                        )}
                        <small className="text-muted">Specialist will contact them here</small>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Vehicle VIN *</label>
                        <input
                          type="text"
                          name="vin"
                          value={formData.vin}
                          onChange={handleInputChange}
                          className={`form-control ${errors.vin ? 'is-invalid' : ''}`}
                          placeholder="1HGCM82633A123456"
                          maxLength="17"
                          style={{ fontFamily: 'monospace' }}
                        />
                        {errors.vin && (
                          <div className="invalid-feedback">{errors.vin}</div>
                        )}
                        <small className="text-muted">17-character Vehicle Identification Number</small>
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-lg w-100"
                        style={{ backgroundColor: '#FF9516', borderColor: '#FF9516', color: 'white' }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Submitting Referral...
                          </>
                        ) : (
                          'Submit Customer Referral'
                        )}
                      </button>
                    </div>

                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        By submitting this referral, you confirm the customer has expressed interest in automotive services.
                      </small>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-vh-100 bg-light">
        {renderHeader()}

        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card shadow">
                <div className="card-body p-5">
                  <div className="text-center mb-5">
                    <div className="text-success mb-3">
                      <i className="fa-sharp fa-solid fa-check-circle fa-3x"></i>
                    </div>
                    <h1 className="h2 mb-3">Referral Submitted Successfully!</h1>
                    <p className="text-muted">
                      Your referral has been added to our system and will be matched with qualified specialists.
                    </p>
                  </div>

                  <div className="alert alert-info">
                    <h4 className="alert-heading mb-3">Referral Details</h4>
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Referral ID:</small>
                        <strong>#{submittedData?.referralId}</strong>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Dealership:</small>
                        <strong>{submittedData?.dealer_name}</strong>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Customer:</small>
                        <strong>{submittedData?.customer_name}</strong>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Referred by:</small>
                        <strong>{submittedData?.referring_email}</strong>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Status:</small>
                        <span className="badge bg-success">Active</span>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-warning">
                    <h4 className="alert-heading mb-3">What Happens Next?</h4>
                    <ol className="mb-0">
                      <li><strong>Specialist Matching</strong> - We'll find qualified specialists in your area</li>
                      <li><strong>Customer Contact</strong> - A specialist will reach out to the customer within 24-48 hours</li>
                      <li><strong>Service Delivery</strong> - The specialist provides quality service to your customer</li>
                      <li><strong>Commission Payment</strong> - You receive commission when the job is completed</li>
                    </ol>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <button
                        onClick={() => {
                          setStep('form');
                          setFormData({
                            referring_email: formData.referring_email,
                            customer_name: '',
                            customer_email: '',
                            vin: ''
                          });
                          setErrors({});
                          setSubmittedData(null);
                        }}
                        className="btn btn-lg w-100"
                        style={{ backgroundColor: '#FF9516', borderColor: '#FF9516', color: 'white' }}
                      >
                        Submit Another Referral
                      </button>
                    </div>
                    <div className="col-md-6 mb-3">
                      <a
                        href="https://jobvious.com"
                        className="btn btn-lg w-100"
                        style={{ backgroundColor: '#FF9516', borderColor: '#FF9516', color: 'white' }}
                      >
                        Learn More About Jobvious
                      </a>
                    </div>
                  </div>

                  <div className="border-top pt-4 text-center">
                    <small className="text-muted">
                      Questions about your referral? Contact us at{' '}
                      <a href="mailto:support@jobvious.com" className="text-decoration-none">
                        support@jobvious.com
                      </a>
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PublicReferralForm;
